import { action } from '@reatom/framework'
import { match } from 'ts-pattern'

import { CurrencyModel } from '@root/entities/currency'
import { Network } from '@root/entities/network'

import { processBuySwapAction, processSellSwapAction } from '../../swap'

import type { WalletSwapInputModel } from './reatom-wallet-swap-input'
import type { WalletSwapPairModel } from './reatom-wallet-swap-pair'
import type { WalletSwapQuoteModel } from './reatom-wallet-swap-quote'
import type { WalletSwapSettingsModel } from './reatom-wallet-swap-settings'
import type { BuySwapArgs } from '../../swap'
import type { WalletModel } from '@root/entities/wallet'

export const processWalletSwapAction = action(
  async (
    ctx,
    {
      inputModel,
      settingsModel,
      pairModel,
      quoteModel,
      wallet,
    }: {
      inputModel: WalletSwapInputModel
      settingsModel: WalletSwapSettingsModel
      pairModel: WalletSwapPairModel
      quoteModel: WalletSwapQuoteModel
      wallet: WalletModel
    },
  ) => {
    const inCurrency = ctx.get(inputModel.inCurrencyAtom)
    const outCurrency = ctx.get(inputModel.outCurrencyAtom)
    const pool = ctx.get(pairModel.selectedPoolAtom)
    const settings = ctx.get(settingsModel.swapSettingsAtom)
    const quote = ctx.get(quoteModel.quoteStateAtom).value
    if (!quote) {
      return
    }

    const inputAmount = quote.inputAmount

    if (!inCurrency || !outCurrency || inputAmount.eq(0) || !pool) {
      return
    }

    const network = pool.network

    const swapType = match(network)
      .returnType<'buy' | 'sell' | undefined>()
      .with(Network.Ethereum, () => {
        if (CurrencyModel.isEthOrWeth(inCurrency)) {
          return 'buy'
        }
        if (CurrencyModel.isEthOrWeth(outCurrency)) {
          return 'sell'
        }
        return undefined
      })
      .with(Network.Base, () => {
        if (CurrencyModel.isBaseEthOrWeth(inCurrency)) {
          return 'buy'
        }
        if (CurrencyModel.isBaseEthOrWeth(outCurrency)) {
          return 'sell'
        }
        return undefined
      })
      .with(Network.Bsc, () => {
        if (CurrencyModel.isBnbOrWbnb(inCurrency)) {
          return 'buy'
        }
        if (CurrencyModel.isBnbOrWbnb(outCurrency)) {
          return 'sell'
        }
        return undefined
      })
      .with(Network.Solana, () => {
        if (CurrencyModel.isSolOrWsol(inCurrency)) {
          return 'buy'
        }
        if (CurrencyModel.isSolOrWsol(outCurrency)) {
          return 'sell'
        }
        return undefined
      })
      .with(Network.Ton, () => {
        if (CurrencyModel.isTon(inCurrency)) {
          return 'buy'
        }
        if (CurrencyModel.isTon(outCurrency)) {
          return 'sell'
        }
        return undefined
      })
      .exhaustive()

    if (!swapType) {
      return
    }

    const requestBase = {
      amount: quote.inputAmount,
      pair: pool,
      wallet,
      settings,
      quote,
    } as BuySwapArgs

    return match(swapType)
      .with('buy', () => processBuySwapAction(ctx, requestBase))
      .with('sell', () => processSellSwapAction(ctx, requestBase))
      .exhaustive()
  },
  'processWalletSwapAction',
)
